import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Building,
  Plus,
  Copy,
  ExternalLink,
  MessageCircle,
} from "lucide-react";
import { getConsistentColor } from "@/lib/utils";
import { AgentCardSkeleton } from "@/components/ui/skeleton";

interface Agent {
  id: string;
  title: string;
  agent_name?: string;
  avatar_url?: string;
  public_id: string;
  is_active: boolean;
}

interface AgentStats {
  total: number;
  last30Days: number;
  last24Hours: number;
}

interface ChatAgentsSectionProps {
  agents: Agent[];
  agentStats: Record<string, AgentStats>;
  isLoading: boolean;
  organizationError: string | null;
  organization: any;
  onCreateAgent: () => void;
  onNavigateToDetail: (id: string) => void;
  onViewConversations: (e: React.MouseEvent, agentId: string) => void;
  onCopyAgentLink: (e: React.MouseEvent, agent: Agent) => void;
  onOpenAgentChat: (e: React.MouseEvent, agent: Agent) => void;
  getAgentGradient: (id: string) => string;
  getInitials: (name: string) => string;
}

export const ChatAgentsSection: React.FC<ChatAgentsSectionProps> = ({
  agents,
  agentStats,
  isLoading,
  organizationError,
  organization,
  onCreateAgent,
  onNavigateToDetail,
  onViewConversations,
  onCopyAgentLink,
  onOpenAgentChat,
  getAgentGradient,
  getInitials,
}) => {
  // Fix race condition: prevent flash of empty state by using a brief delay
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (
      !isLoading &&
      agents.length === 0 &&
      isInitialLoad &&
      !organizationError
    ) {
      // Small delay to prevent race condition flash
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 50);
      return () => clearTimeout(timer);
    } else if (agents.length > 0) {
      setIsInitialLoad(false);
    } else if (isLoading) {
      setIsInitialLoad(true);
    }
  }, [isLoading, agents.length, isInitialLoad, organizationError]);

  const shouldShowLoading =
    isLoading || (isInitialLoad && agents.length === 0 && !organizationError);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageCircle className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-semibold">Chat Agents</h2>
        </div>
      </div>

      {/* Chat Agents Content */}
      {shouldShowLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <AgentCardSkeleton key={i} />
          ))}
        </div>
      ) : organizationError ? null : agents.length === 0 ? (
        <Card className="bg-muted/50">
          <CardContent className="py-12 flex flex-col items-center justify-center">
            <Building className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No chat agents yet</h3>
            <p className="text-muted-foreground mb-6 text-center max-w-md">
              Create your first AI chat agent to help automate customer
              interactions and provide valuable assistance.
            </p>
            <Button onClick={onCreateAgent} disabled={!organization}>
              <Plus className="mr-2 h-4 w-4" />
              Create Chat Agent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {agents.map((agent) => {
            const stats = agentStats[agent.id] || {
              total: 0,
              last30Days: 0,
              last24Hours: 0,
            };
            const isActive = agent.is_active;
            const gradientClass = getAgentGradient(agent.id);

            return (
              <Card
                key={agent.id}
                className="group hover:shadow-md transition-all duration-300 cursor-pointer border border-gray-200 hover:border-gray-300 overflow-hidden"
                onClick={() => onNavigateToDetail(agent.id)}
              >
                {/* Colored banner */}
                <div
                  className={`h-20 w-full ${gradientClass.replace("bg-gradient-to-br", "bg-gradient-to-r")}`}
                ></div>

                <CardContent className="p-5 relative">
                  {/* Avatar overlapping banner */}
                  <div className="absolute -top-12 left-5">
                    <div
                      className={`relative p-0.5 rounded-full ${gradientClass}`}
                    >
                      <Avatar className="h-32 w-32 border-2 border-white">
                        <AvatarImage src={agent.avatar_url || ""} />
                        <AvatarFallback
                          className="text-xl font-bold text-white"
                          style={{
                            backgroundColor: `${getConsistentColor(agent.id)}80`,
                          }}
                        >
                          {getInitials(agent.agent_name || agent.title)}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  </div>

                  {/* Content - left aligned */}
                  <div className="text-left">
                    {/* Avatar and Agent Info Row */}
                    <div className="flex items-center gap-6 mb-8">
                      {/* Space for avatar (since it's positioned absolute) */}
                      <div className="w-32 flex-shrink-0"></div>

                      {/* Agent Info Column */}
                      <div className="flex-1 min-w-0">
                        {/* Main Title (Agent Name) */}
                        <h3 className="text-2xl font-bold text-gray-900 truncate">
                          {agent.agent_name || "Set a name"}
                        </h3>

                        {/* Subtitle (Title/Description) */}
                        <p className="text-base text-gray-600 truncate">
                          {agent.title || "Set a title"}
                        </p>
                      </div>
                    </div>

                    {/* Stats without header */}
                    <div className="mb-8">
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">
                            Total conv.
                          </div>
                          <div className="text-base font-medium text-gray-900">
                            {stats.total}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">
                            Last 30 days
                          </div>
                          <div className="text-base font-medium text-gray-900">
                            {stats.last30Days}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 mb-1">
                            Last 24h
                          </div>
                          <div className="text-base font-medium text-gray-900">
                            {stats.last24Hours}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={(e) => onViewConversations(e, agent.id)}
                        variant="outline"
                        size="sm"
                      >
                        View Conversations
                      </Button>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => onCopyAgentLink(e, agent)}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Copy agent link</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => onOpenAgentChat(e, agent)}
                              className="text-gray-600 hover:text-gray-800"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Preview agent</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
